/**
 * piquelife.com's market popup — snippets/component-market-popup.liquid. Cookie names, lifetimes and the 500ms delay
 * are live's (snippets/markets-switcher-popup.liquid in the live theme).
 *
 * - Auto-open: a visitor whose country is in `data-auto-open` and who has no `piqueMarketPopupSeen` cookie sees the
 *   popup 500ms after load, and the cookie is set for 7 days then. Its buttons post the localization form natively.
 * - Confirm: when the page loads without a `piqueMarketConfirmed` cookie, a click on a header currency option
 *   (`[data-market-confirm]`) opens the popup for that market instead of switching, and sets that cookie for 365 days
 *   and `piqueMarketPopupSeen` for 7. Continue then submits the picked option, the link the selector's USD option.
 *
 * @extends HTMLElement
 */

const SEEN_COOKIE = 'piqueMarketPopupSeen';
const CONFIRMED_COOKIE = 'piqueMarketConfirmed';
const SEEN_DAYS = 7;
const CONFIRMED_DAYS = 365;
const OPEN_DELAY = 500;
const GLOBAL_CURRENCY = 'USD';

/**
 * @param {string} name
 * @returns {string|undefined}
 */
function getCookie(name) {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1];
}

/**
 * @param {string} name
 * @param {string} value
 * @param {number} days
 */
function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value};expires=${expires};path=/;SameSite=Lax`;
}

export class MarketPopup extends HTMLElement {
  timer = 0;
  opener = null;
  pendingOption = null;
  defaults = null;

  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onClose = this.onClose.bind(this);
  }

  connectedCallback() {
    if (!this.dialog) return;

    this.defaults = {
      flag: this.flag?.textContent ?? '',
      heading: this.heading?.textContent ?? '',
      continueLabel: this.continueButton?.textContent ?? '',
    };

    this.addEventListener('click', this.onClick);
    this.dialog.addEventListener('close', this.onClose);

    /* Live decides once, when the page loads, whether the selector asks first. */
    if (!getCookie(CONFIRMED_COOKIE)) document.addEventListener('click', this.onDocumentClick);

    this.scheduleAutoOpen();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.removeEventListener('click', this.onClick);
    this.dialog?.removeEventListener('close', this.onClose);
    document.removeEventListener('click', this.onDocumentClick);
  }

  get dialog() {
    return this.querySelector('dialog');
  }

  get flag() {
    return this.querySelector('[data-market-popup-flag]');
  }

  get heading() {
    return this.querySelector('[data-market-popup-heading]');
  }

  get continueButton() {
    return this.querySelector('[data-market-popup-continue]');
  }

  scheduleAutoOpen() {
    const countries = (this.dataset.autoOpen || '').split(',');
    if (!countries.includes(this.dataset.country)) return;
    if (getCookie(SEEN_COOKIE) || window.Shopify?.designMode) return;

    this.timer = setTimeout(() => {
      if (!this.dialog.open) {
        this.pendingOption = null;
        this.populate(this.defaults);
        this.open(null);
      }
      setCookie(SEEN_COOKIE, '1', SEEN_DAYS);
    }, OPEN_DELAY);
  }

  /** A header currency option: ask before switching, naming the market it switches to. */
  onDocumentClick(event) {
    const option = event.target.closest?.('[data-market-confirm]');
    if (!option?.value || !option.form) return;

    event.preventDefault();
    const name = option.dataset.marketName || option.value;
    this.pendingOption = option;
    this.populate({
      flag: option.dataset.marketFlag || '',
      heading: this.fill(this.dataset.heading, name),
      continueLabel: this.fill(this.dataset.continue, name),
    });

    setCookie(CONFIRMED_COOKIE, '1', CONFIRMED_DAYS);
    /* So the auto-open does not fire again straight after this switch, as on live. */
    setCookie(SEEN_COOKIE, '1', SEEN_DAYS);

    const selector = option.closest('details');
    selector?.removeAttribute('open');
    this.open(selector?.querySelector('summary') || option);
  }

  onClick(event) {
    if (event.target.closest('[data-market-popup-close]')) {
      this.dialog.close();
      return;
    }

    /* The panel fills the dialog, so a click on the dialog itself is a click on the backdrop. */
    if (event.target === this.dialog) {
      this.dialog.close();
      return;
    }

    /* Auto-opened, the buttons post this popup's own form as they are. */
    const choice = event.target.closest('[data-market-popup-continue], [data-market-popup-global]');
    if (!choice || !this.pendingOption) return;

    const option = choice.hasAttribute('data-market-popup-continue')
      ? this.pendingOption
      : this.pendingOption.form.querySelector(`[data-market-confirm][value="${GLOBAL_CURRENCY}"]`);
    if (!option) return;

    event.preventDefault();
    option.form.requestSubmit(option);
  }

  onClose() {
    this.pendingOption = null;
    /* showModal() restores focus itself; this covers where it could not, such as a selector closed meanwhile. */
    const active = document.activeElement;
    const settled = active && active !== document.body && active !== document.documentElement;
    if (!settled || this.dialog.contains(active)) this.opener?.focus();
    this.opener = null;
  }

  /**
   * @param {HTMLElement|null} opener - The control to hand focus back to
   */
  open(opener) {
    if (this.dialog.open) return;
    this.opener = opener;
    this.dialog.showModal();
    /* The dialog, not its first button: a screen reader reads its name and message, and no button shows a ring. */
    this.dialog.focus();
  }

  /**
   * @param {{ flag: string, heading: string, continueLabel: string }} copy
   */
  populate({ flag, heading, continueLabel }) {
    if (this.flag) this.flag.textContent = flag;
    if (this.heading) this.heading.textContent = heading;
    if (this.continueButton) this.continueButton.textContent = continueLabel;
  }

  /** Swaps the [country] marker in a translated string, as text. */
  fill(template = '', name = '') {
    return template.replace('[country]', () => name);
  }
}

if (!customElements.get('market-popup')) {
  customElements.define('market-popup', MarketPopup);
}
